/**
 * The closed workspace URL contract: every canonical template, its permanent `/v2` compatibility
 * twin, and every historical alias that must keep resolving.
 *
 * Matching is table-driven and case-sensitive. A pathname is a workspace route only when it
 * matches a template declared here — never because it merely starts with a known prefix. Prefix
 * matching is what lets `/v2evil` and `/v2-impersonator` masquerade as product surfaces.
 */

/** The `/v2` namespace is a permanent compatibility presentation, not a deprecated one. */
export const WORKSPACE_COMPATIBILITY_SEGMENT = 'v2';

/** Presentations a destination can be rendered as. Input may additionally be `legacy`. */
export type WorkspacePresentation = 'canonical' | 'compatibility';
export type WorkspacePathPresentation = WorkspacePresentation | 'legacy';

/**
 * The presentation browser surfaces write and display: the gate normalises the address bar to it
 * and the in-app writers emit it. Native deep links, the desktop bridge and every server producer
 * keep the `/v2` compatibility form as their wire contract whatever this value is.
 */
export const PREFERRED_WORKSPACE_PRESENTATION: WorkspacePresentation = 'canonical';

export const ADS_DETAIL_SECTIONS = ['advertiser', 'entity', 'disclaimer', 'party'] as const;
export type AdsDetailSection = (typeof ADS_DETAIL_SECTIONS)[number];

export type WorkspaceDynamicFamily =
  | 'person'
  | 'organisation'
  | 'task-detail'
  | 'document-detail'
  | 'ads-detail'
  | 'broadcast-builder'
  | 'composition';

export type WorkspaceStaticFamily =
  | 'dashboard'
  | 'correspondence'
  | 'people'
  | 'organisation-list'
  | 'knowledge'
  | 'pulse-ai'
  | 'notifications'
  | 'settings'
  | 'settings-email-accounts'
  | 'settings-senders'
  | 'tasks'
  | 'analytics'
  | 'explorer'
  | 'ads-explorer'
  | 'creative'
  | 'clip-studio'
  | 'quick-drafts'
  | 'discover'
  | 'websites'
  | 'automations'
  | 'broadcasts';

export type WorkspaceRouteFamily = WorkspaceStaticFamily | WorkspaceDynamicFamily;

/** A resolved workspace destination: semantic identity, independent of presentation. */
export type WorkspaceDestination =
  | { readonly family: WorkspaceStaticFamily }
  | { readonly family: 'person'; readonly personId: string }
  | { readonly family: 'organisation'; readonly organisationId: string }
  | { readonly family: 'task-detail'; readonly taskId: string }
  | { readonly family: 'document-detail'; readonly documentId: string }
  | { readonly family: 'ads-detail'; readonly section: AdsDetailSection; readonly key: string }
  | { readonly family: 'broadcast-builder'; readonly broadcastId: string }
  | {
      readonly family: 'composition';
      readonly projectId: string;
      readonly compositionId: string;
    };

/**
 * Root segments owned by public, auth, native, admin, and rewrite surfaces. Capturing any of them
 * into the workspace would hand a staff-app route to a constituent-facing URL — `/u` and `/p` are
 * Vercel rewrites to the public unsubscribe and preference-centre endpoints, so a constituent
 * tapping unsubscribe would land in the staff app instead.
 */
export const RESERVED_ROOT_SEGMENTS: ReadonlySet<string> = new Set([
  'admin',
  'ad',
  'auth',
  'helpdesk',
  'landing',
  'login',
  'm',
  'onboarding',
  'organizations',
  'p',
  'print',
  'request-access',
  'share',
  'u',
  'welcome',
]);

/** Families named more than twice below; hoisted so the parse and render sites cannot drift. */
const PEOPLE = 'people';
const PERSON = 'person';
const ORGANISATION_LIST = 'organisation-list';
const BROADCAST_BUILDER = 'broadcast-builder';
const ADS_DETAIL = 'ads-detail';
const PEOPLE_PATH = '/people';
const SETTINGS_PATH = '/settings';
const PERSON_ID_PLACEHOLDER = '/:personId';
const ORGANISATION_LIST_PATH = '/organisations';
/**
 * The connected composition workspace. Two dynamic segments, so it is also the
 * proof that the builders/parser handle multi-parameter families — the ads
 * variant's second parameter is a literal section, not a free identifier.
 */
const COMPOSITION_PATH = '/clip-studio/projects/:projectId/compositions/:compositionId';

interface TemplateShape {
  /** Literal segments, or `:name` for a dynamic one. Empty for the site root. */
  readonly segments: readonly string[];
  readonly presentation: WorkspacePathPresentation;
}

/** The ads-detail variant carries its section as data because the section is a literal segment. */
export type WorkspaceRouteTemplate =
  | (TemplateShape & { readonly family: Exclude<WorkspaceRouteFamily, 'ads-detail'> })
  | (TemplateShape & { readonly family: 'ads-detail'; readonly section: AdsDetailSection });

interface SeedShape {
  /** Template path, `:name` marking a dynamic segment. */
  readonly path: string;
  /** `false` for V1-era root aliases (`/dashboard`, `/constituents`, …) with no `/v2` form. */
  readonly hasCompatibilityTwin?: boolean;
}

type TemplateSeed =
  | (SeedShape & { readonly family: Exclude<WorkspaceRouteFamily, 'ads-detail'> })
  | (SeedShape & { readonly family: 'ads-detail'; readonly section: AdsDetailSection });

function toSegments(path: string): readonly string[] {
  return path === '/' ? [] : path.slice(1).split('/');
}

const ADS_SEEDS: readonly TemplateSeed[] = ADS_DETAIL_SECTIONS.map((section) => ({
  path: `/ads/${section}/:key`,
  family: ADS_DETAIL,
  section,
}));

const CANONICAL_SEEDS: readonly TemplateSeed[] = [
  { path: '/', family: 'dashboard' },
  { path: '/correspondence', family: 'correspondence' },
  { path: PEOPLE_PATH, family: PEOPLE },
  { path: '/people/:personId', family: PERSON },
  { path: '/people/organisations/:organisationId', family: 'organisation' },
  { path: '/knowledge', family: 'knowledge' },
  { path: '/pulse-ai', family: 'pulse-ai' },
  { path: '/notifications', family: 'notifications' },
  { path: SETTINGS_PATH, family: 'settings' },
  { path: '/tasks', family: 'tasks' },
  { path: '/tasks/:taskId', family: 'task-detail' },
  // Under /knowledge, which is an exact-match static template, so the deeper
  // path cannot be swallowed by it.
  { path: '/knowledge/documents/:documentId', family: 'document-detail' },
  { path: '/analytics', family: 'analytics' },
  { path: '/explorer', family: 'explorer' },
  { path: '/ads-explorer', family: 'ads-explorer' },
  ...ADS_SEEDS,
  { path: '/creative', family: 'creative' },
  { path: '/clip-studio', family: 'clip-studio' },
  { path: COMPOSITION_PATH, family: 'composition' },
  { path: '/quick-drafts', family: 'quick-drafts' },
  { path: '/discover', family: 'discover' },
  { path: '/automations', family: 'automations' },
  { path: '/websites', family: 'websites' },
  { path: '/campaigns', family: 'broadcasts' },
  { path: '/campaigns/:broadcastId', family: BROADCAST_BUILDER },
];

/**
 * Unprefixed historical aliases, minus the organisation-list literals derived below. A literal that
 * shadows a placeholder must exist at EVERY root that placeholder lives under, and in every
 * presentation that root has, which is why `/tasks/archive` and `/correspondence/catch-up` carry
 * `/v2` twins they never had in the wild.
 */
const LEGACY_ROOT_SEEDS: readonly TemplateSeed[] = [
  { path: '/dashboard', family: 'dashboard', hasCompatibilityTwin: false },
  { path: '/correspondence/catch-up', family: 'correspondence' },
  { path: '/constituents', family: PEOPLE, hasCompatibilityTwin: false },
  { path: '/constituents/:personId', family: PERSON, hasCompatibilityTwin: false },
  { path: '/settings/email-accounts', family: 'settings-email-accounts' },
  { path: '/settings/sending-domains', family: 'settings-senders' },
  { path: '/user/settings', family: 'settings', hasCompatibilityTwin: false },
  { path: '/tasks/archive', family: 'tasks' },
  { path: '/broadcasts/:broadcastId', family: BROADCAST_BUILDER, hasCompatibilityTwin: false },
];

/** Aliases that only ever existed under `/v2`. */
const COMPATIBILITY_ONLY_ROOT_SEEDS: readonly TemplateSeed[] = [
  { path: '/crm', family: PEOPLE },
  { path: '/crm/:personId', family: PERSON },
  { path: '/crm/organisations/:organisationId', family: 'organisation' },
  { path: '/organisations', family: ORGANISATION_LIST },
];

/**
 * The organisation-list literal is DERIVED from the person placeholder it shadows, at every root,
 * rather than declared alongside it - declaring the pair by hand is what leaves one root behind.
 * A root missing its literal resolves `<root>/organisations` as a person whose id is the string
 * `organisations`, which canonicalises to `/people/organisations`, which parses AGAIN as the
 * organisation list: one input, two history replaces, three locations. Deriving makes a new alias
 * root carry its literal in by construction.
 */
function organisationListAliases(seeds: readonly TemplateSeed[]): readonly TemplateSeed[] {
  return seeds
    .filter((seed) => seed.family === PERSON)
    .map((seed) => ({
      path: seed.path.replace(PERSON_ID_PLACEHOLDER, ORGANISATION_LIST_PATH),
      family: ORGANISATION_LIST,
      hasCompatibilityTwin: seed.hasCompatibilityTwin !== false,
    }));
}

const LEGACY_SEEDS: readonly TemplateSeed[] = [
  ...LEGACY_ROOT_SEEDS,
  ...organisationListAliases([...CANONICAL_SEEDS, ...LEGACY_ROOT_SEEDS]),
];

const COMPATIBILITY_ONLY_SEEDS: readonly TemplateSeed[] = [
  ...COMPATIBILITY_ONLY_ROOT_SEEDS,
  ...organisationListAliases(COMPATIBILITY_ONLY_ROOT_SEEDS),
];

function toTemplate(
  seed: TemplateSeed,
  presentation: WorkspacePathPresentation,
  prefixed: boolean,
): WorkspaceRouteTemplate {
  const split = toSegments(seed.path);
  const segments = prefixed ? [WORKSPACE_COMPATIBILITY_SEGMENT, ...split] : split;
  return seed.family === ADS_DETAIL
    ? { segments, presentation, family: seed.family, section: seed.section }
    : { segments, presentation, family: seed.family };
}

function dynamicSegmentCount(template: WorkspaceRouteTemplate): number {
  return template.segments.filter((segment) => segment.startsWith(':')).length;
}

/**
 * Match order. Templates only ever compete at equal segment length, so the one ordering rule that
 * matters is literals before placeholders — `/people/organisations` must reach the organisation
 * list, not a person whose id is `organisations`, and `/tasks/archive` must reach the task list.
 * The sort is stable, so the declaration order above breaks ties within one specificity level.
 */
export const WORKSPACE_ROUTE_TEMPLATES: readonly WorkspaceRouteTemplate[] = [
  ...CANONICAL_SEEDS.map((seed) => toTemplate(seed, 'compatibility', true)),
  ...LEGACY_SEEDS.filter((seed) => seed.hasCompatibilityTwin !== false).map((seed) =>
    toTemplate(seed, 'legacy', true),
  ),
  ...COMPATIBILITY_ONLY_SEEDS.map((seed) => toTemplate(seed, 'legacy', true)),
  ...CANONICAL_SEEDS.map((seed) => toTemplate(seed, 'canonical', false)),
  ...LEGACY_SEEDS.map((seed) => toTemplate(seed, 'legacy', false)),
].sort((a, b) => dynamicSegmentCount(a) - dynamicSegmentCount(b));

/**
 * Prefix aliases collapse an entire subtree onto one destination. `/v2/reporting/*` mirrors the
 * router and the desktop window-state restore, which both treat any depth below the pre-rename
 * Analytics root as Analytics.
 */
export const WORKSPACE_PREFIX_ALIASES: readonly {
  readonly segments: readonly string[];
  readonly family: WorkspaceStaticFamily;
}[] = [{ segments: toSegments('/v2/reporting'), family: 'analytics' }];

export interface WorkspaceFamilyContract {
  /** Canonical path template; `:name` placeholders are filled from the destination, in order. */
  readonly canonicalSegments: readonly string[];
  /**
   * Query keys that carry the destination's identity rather than user state. Aliases such as
   * `/people/organisations` encode in the path what the canonical URL encodes in the query, so
   * rendering the destination must write these keys back.
   */
  readonly identityQuery?: readonly (readonly [string, string])[];
}

function contract(
  path: string,
  identityQuery?: readonly (readonly [string, string])[],
): WorkspaceFamilyContract {
  return identityQuery
    ? { canonicalSegments: toSegments(path), identityQuery }
    : { canonicalSegments: toSegments(path) };
}

export const WORKSPACE_FAMILY_CONTRACTS: Readonly<
  Record<WorkspaceRouteFamily, WorkspaceFamilyContract>
> = {
  dashboard: contract('/'),
  correspondence: contract('/correspondence'),
  people: contract(PEOPLE_PATH),
  person: contract('/people/:personId'),
  'organisation-list': contract(PEOPLE_PATH, [['type', 'organisations']]),
  organisation: contract('/people/organisations/:organisationId'),
  knowledge: contract('/knowledge'),
  'pulse-ai': contract('/pulse-ai'),
  notifications: contract('/notifications'),
  settings: contract(SETTINGS_PATH),
  'settings-email-accounts': contract(SETTINGS_PATH, [['tab', 'email-settings']]),
  'settings-senders': contract(SETTINGS_PATH, [
    ['tab', 'broadcasts'],
    ['subtab', 'senders'],
  ]),
  tasks: contract('/tasks'),
  'task-detail': contract('/tasks/:taskId'),
  'document-detail': contract('/knowledge/documents/:documentId'),
  analytics: contract('/analytics'),
  explorer: contract('/explorer'),
  'ads-explorer': contract('/ads-explorer'),
  'ads-detail': contract('/ads/:section/:key'),
  creative: contract('/creative'),
  'clip-studio': contract('/clip-studio'),
  composition: contract(COMPOSITION_PATH),
  'quick-drafts': contract('/quick-drafts'),
  discover: contract('/discover'),
  automations: contract('/automations'),
  websites: contract('/websites'),
  broadcasts: contract('/campaigns'),
  'broadcast-builder': contract('/campaigns/:broadcastId'),
};
